"use client"

import { getFixedT } from "@/languages/i18n"
import { CareerListResponseDataItem } from "@/types/codegen/data-contracts"
import { Alert, Form } from "antd"
import Link from "next/link"
import { useRef, useState } from "react"

type FormApplyProps = {
  jobDetail?: CareerListResponseDataItem
  locale: string
}

const FormApply: React.FC<FormApplyProps> = ({ jobDetail, locale }) => {
  const t = getFixedT(locale)
  const [form] = Form.useForm()

  const [showAlert, setShowAlert] = useState(false)
  const [isMessageSent, setIsMessageSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleUploadClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 3 * 1024 * 1024) {
      alert(t("Must be less than 3MB"))
      return
    }

    setSelectedFile(file)
    form.setFieldsValue({ cvFileUpload: file })
  }

  const onFinish = async (values: any) => {
    const { fullname, phone, email, selfInstruction } = values
    try {
      setLoading(true)

      const formData = new FormData()
      formData.append("fullname", fullname)
      formData.append("phone", phone)
      if (email) formData.append("email", email)
      if (selfInstruction) formData.append("selfInstruction", selfInstruction)
      // Trang tuyển dụng chỉ thuộc tamdagroup.eu — cố định domain, không suy ra từ host (tránh ra tamdamedia.eu)
      formData.append("domain", "tamdagroup.eu")
      if (jobDetail?.id != null) formData.append("career", String(jobDetail.id))
      if (selectedFile) formData.append("cv", selectedFile)

      // Một request duy nhất: server lo upload CV → lưu hồ sơ → gửi mail
      const response = await fetch("/api/career-registration", { method: "POST", body: formData })
      if (!response.ok) throw new Error("Failed to submit career registration")

      setIsMessageSent(true)
      form.resetFields()
      setSelectedFile(undefined)
    } catch (e) {
      console.error(e)
      setIsMessageSent(false)
    } finally {
      setLoading(false)
      setShowAlert(true)
    }
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish} className="space-y-4">
        <p className="mb-4 text-[#333] text-base sm:text-lg">
          <i>{t("require") + " *"}</i>
        </p>

        {/* Full name */}
        <Form.Item
          name="fullname"
          className="text-base"
          label={<span className="text-[#333] text-base">{t("Full Name")}</span>}
          rules={[
            {
              required: true,
              message: t("Cannot be empty"),
            },
          ]}
        >
          <input type="text" placeholder="John Doe" className="form-control placeholder:text-[#6c757d]" />
        </Form.Item>

        {/* Phone */}
        <Form.Item
          label={<span className="text-[#333] text-base">{t("Phone number")}</span>}
          name="phone"
          rules={[
            {
              required: true,
              message: t("Cannot be empty"),
            },
            { pattern: /^[a-zA-Z0-9]+$/, message: t("Số điện thoại phải là 0-9") },
          ]}
        >
          <input type="text" placeholder={t("Phone number")} className="form-control placeholder:text-[#6c757d]" />
        </Form.Item>

        {/* Email */}
        <Form.Item
          label={<span className="text-[#333] text-base">{t("Email address")}</span>}
          name="email"
          rules={[
            {
              required: true,
              message: t("Cannot be empty"),
            },
            {
              pattern:
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              message: t("Please enter a valid email"),
            },
          ]}
        >
          <input type="text" placeholder={t("Email address")} className="form-control placeholder:text-[#6c757d]" />
        </Form.Item>

        {/* Self introduction */}
        <Form.Item
          label={<span className="text-[#333] text-base">{t("Self introduction")}</span>}
          name="selfInstruction"
        >
          <textarea rows={3} placeholder={`${t("Tell us about yourself")}...`} className="form-control" />
        </Form.Item>

        {/* Download forms */}
        <div className="text-lg text-[#333]">
          {t("Please download and fill in the job application form then attach it to the file below")}
          <br />
          <Link href="#" className="hover:text-[#157e40] hover:filter-[brightness(1.1)]">
            {t("Vietnamese form")}
          </Link>
          <br />
          <Link href="#" className=" hover:text-[#157e40] hover:filter-[brightness(1.1)]">
            {t("English form")}
          </Link>
          <br />
          <Link
            href="https://d300nzfp1n1jsy.cloudfront.net/Job_application_form_ac4fa1e795.xlsx"
            className="text-[#18af4e] hover:text-[#157e40] hover:filter-[brightness(1.1)]"
          >
            {t("Czech form")}
          </Link>
        </div>

        {/* File upload (Antd rule, input thường) */}
        <Form.Item
          name="cvFileUpload"
          rules={[
            {
              required: true,
              message: t("Please upload your CV here"),
            },
          ]}
        >
          <div className="mt-4 flex items-center">
            <input
              type="file"
              accept=".doc,.docx,.pdf,.png,.jpg,.jpeg,.xlsx"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <p className="text-lg text-[#333] mr-5">{t("Upload Application Form")} *</p>
            <button
              onClick={handleUploadClick}
              className="axil-button btn-large btn-transparent !h-9 !leading-none !w-auto"
            >
              <span className="button-text">{t("Upload")}</span>
            </button>
          </div>
        </Form.Item>
        {selectedFile && <p className="text-lg text-[#18af4e]">{selectedFile.name}</p>}

        {/* Policy checkbox */}
        <Form.Item
          name="policy"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("You must accept our terms of service and Privacy Policy"))),
            },
          ]}
        >
          <div className="mt-4 flex items-start gap-2">
            <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <p className="text-[15px] text-[#333] leading-[26px]">
              {t("I agree with Tamda Group’s")}{" "}
              <Link href="/about-us" className="text-[#18af4e] hover:text-[#18af4e]">
                {t("Privacy Policy")}
              </Link>{" "}
              &{" "}
              <Link href="/about-us" className="text-[#18af4e] hover:text-[#18af4e]">
                {t("Terms of Use")}
              </Link>
              .
            </p>
          </div>
        </Form.Item>

        {/* Submit */}
        <div className="!my-[30px]">
          <button type="submit" disabled={loading} className="axil-button btn-large btn-transparent w-full">
            <span className="button-text">{t("Submit")}</span>
            <span className="button-icon"></span>
          </button>
        </div>
      </Form>

      {showAlert && (
        <div className="mt-4">
          <Alert
            description={
              isMessageSent
                ? t("Submitted, thanks for your apply and We will keep you in touch")
                : t("Something went wrong")
            }
            type={isMessageSent ? "success" : "error"}
          />
        </div>
      )}
    </>
  )
}

export default FormApply
